import React, { useRef, useEffect, forwardRef } from 'react'
import { usePhoneMask } from '@/hooks/usePhoneMask'
import { FormLabel } from '@/components/ui/forms'
import { cn } from '@/utilities/ui'

interface FormPhoneInputProps {
  id: string
  label: string
  value: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
  inputClassName?: string
  labelClassName?: string
  errorClassName?: string
  requiredClassName?: string
  showErrorBorder?: boolean
  disableFocusRing?: boolean
  variant?: 'outlined' | 'filled'
  size?: 'small' | 'medium' | 'large'
  fullWidth?: boolean
  testId?: string
  dataTestId?: string
  maxLength?: number
  onChange: (value: string) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
}

export const FormPhoneInput = forwardRef<HTMLInputElement, FormPhoneInputProps>(
  (
    {
      id,
      label,
      value,
      placeholder = '+1 (123) 456-7890',
      required = false,
      disabled = false,
      error,
      inputClassName = '',
      labelClassName = '',
      showErrorBorder = true,
      disableFocusRing = false,
      errorClassName = '',
      requiredClassName = '',
      variant = 'outlined',
      size = 'medium',
      fullWidth = false,
      testId,
      dataTestId,
      maxLength,
      onChange,
      onBlur,
    },
    ref,
  ) => {
    const resolvedTestId = dataTestId ?? testId
    const inputRef = useRef<HTMLInputElement>(null)
    const { createMask, setValue } = usePhoneMask({
      onAccept: (unmaskedValue) => {
        onChange(unmaskedValue)
      },
    })

    useEffect(() => {
      if (inputRef.current) {
        createMask(inputRef.current)
      }
    }, [createMask])

    useEffect(() => {
      setValue(value)
    }, [value, setValue])

    const hasValue = Boolean(value && value.trim())

    // Base classes (same as FormInput)
    const baseInputClasses = [
      'w-full px-3 py-2 border rounded-md transition-colors duration-200',
      'focus:outline-none',
      disableFocusRing ? '' : 'focus:ring-2 focus:ring-offset-0',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'readonly:bg-gray-50',
      hasValue ? '' : 'text-tenant-description-text',
      'placeholder:text-tenant-description-text',
    ]

    // Variant classes
    const variantClasses = {
      outlined: disableFocusRing
        ? 'border-gray-300'
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
      filled: disableFocusRing
        ? 'border-transparent bg-gray-100 focus:bg-white'
        : 'border-transparent bg-gray-100 focus:bg-white focus:border-blue-500 focus:ring-blue-500',
      standard: 'border-b-2 border-gray-300 rounded-none focus:border-blue-500',
    }

    // Size classes
    const sizeClasses = {
      small: 'text-sm py-1.5',
      medium: 'text-base py-2',
      large: 'text-lg py-3',
    }

    // Error classes
    const errorClasses =
      error && showErrorBorder ? 'border-error focus:border-error focus:ring-error' : ''

    // Width classes
    const widthClasses = fullWidth ? 'w-full' : ''

    const inputClasses = [
      ...baseInputClasses,
      variantClasses[variant],
      sizeClasses[size],
      errorClasses,
      widthClasses,
      inputClassName,
    ].join(' ')

    return (
      <div className={`${fullWidth ? 'w-full' : ''}`}>
        <FormLabel
          htmlFor={id}
          className={cn('block text-sm mb-1', labelClassName)}
          required={required}
          requiredIndicatorClassName={requiredClassName}
        >
          {label}
        </FormLabel>
        <input
          ref={(node) => {
            // Handle both refs
            if (typeof ref === 'function') {
              ref(node)
            } else if (ref) {
              ref.current = node
            }
            inputRef.current = node
          }}
          id={id}
          data-testid={resolvedTestId}
          type="tel"
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          maxLength={maxLength}
          onBlur={(e) => onBlur?.(e)}
          className={inputClasses}
        />
        {error && (
          <div className={errorClassName || 'text-red-600 text-xs mt-0.5'} role="alert">
            {error}
          </div>
        )}
      </div>
    )
  },
)

FormPhoneInput.displayName = 'FormPhoneInput'
